/**
 * ContractorsLayout Component
 *
 * Main layout with tab navigation for the contractors module.
 * Admin sees all tabs; contractors see only their own data tabs.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ContractorsList } from './ContractorsList';
import { ContractsList } from './ContractsList';
import { ProjectsList } from './ProjectsList';
import { TimeEntryList } from './TimeEntryList';
import { InvoicesList } from './InvoicesList';
import { ScheduleEditor } from './ScheduleEditor';
import { AvailabilityCalendar } from './AvailabilityCalendar';
import { TimeEntryReview } from './TimeEntryReview';
import { ScheduleReview } from './ScheduleReview';

interface TabDef {
  id: string;
  labelKey: string;
  icon: string;
  adminOnly: boolean;
}

const ADMIN_TABS: TabDef[] = [
  { id: 'contractors', labelKey: 'tabs.contractors', icon: 'U', adminOnly: true },
  { id: 'contracts', labelKey: 'tabs.contracts', icon: 'C', adminOnly: false },
  { id: 'projects', labelKey: 'tabs.projects', icon: 'P', adminOnly: true },
  { id: 'time', labelKey: 'tabs.timeTracking', icon: 'T', adminOnly: false },
  { id: 'invoices', labelKey: 'tabs.invoices', icon: 'I', adminOnly: false },
  { id: 'schedules', labelKey: 'tabs.schedules', icon: 'S', adminOnly: false },
  { id: 'availability', labelKey: 'tabs.availability', icon: 'A', adminOnly: false },
];

const CONTRACTOR_TABS: TabDef[] = [
  { id: 'contracts', labelKey: 'tabs.myContracts', icon: 'C', adminOnly: false },
  { id: 'time', labelKey: 'tabs.myTime', icon: 'T', adminOnly: false },
  { id: 'invoices', labelKey: 'tabs.myInvoices', icon: 'I', adminOnly: false },
  { id: 'schedules', labelKey: 'tabs.mySchedule', icon: 'S', adminOnly: false },
  { id: 'availability', labelKey: 'tabs.myAvailability', icon: 'A', adminOnly: false },
];

export default function ContractorsLayout(): React.JSX.Element {
  const { t } = useTranslation('contractors');
  const navigate = useNavigate();
  const { user } = useAuth();

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || user?.is_superadmin === true;
  const tabs = isAdmin ? ADMIN_TABS : CONTRACTOR_TABS;

  const [activeTab, setActiveTab] = useState<string>(tabs[0].id);

  // Reset active tab when role changes
  useEffect(() => {
    if (!tabs.find(tab => tab.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [isAdmin, activeTab, tabs]);

  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId);
  }, []);

  const renderContent = (): React.JSX.Element => {
    switch (activeTab) {
      case 'contractors':
        return <ContractorsList />;
      case 'contracts':
        return <ContractsList isAdmin={isAdmin} />;
      case 'projects':
        return <ProjectsList />;
      case 'time':
        return isAdmin ? <TimeEntryReview /> : <TimeEntryList />;
      case 'invoices':
        return <InvoicesList isAdmin={isAdmin} />;
      case 'schedules':
        return isAdmin ? <ScheduleReview /> : <ScheduleEditor />;
      case 'availability':
        return <AvailabilityCalendar isAdmin={isAdmin} />;
      default:
        return <ContractorsList />;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Module header with back navigation */}
      <header className="bg-white border-b border-gray-200">
        <div className="px-4 py-2 flex items-center gap-3">
          <button
            onClick={() => navigate('/voice')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('backToHome')}
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-semibold text-gray-900">{t('title')}</h1>
        </div>
      </header>

      {/* Tab bar */}
      <div className="border-b border-gray-200 bg-white">
        <nav className="flex overflow-x-auto scrollbar-hide -mb-px" aria-label={t('navigation')}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`
                flex-shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 text-xs font-bold text-gray-600">
                  {tab.icon}
                </span>
                <span className="whitespace-nowrap">{t(tab.labelKey)}</span>
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-auto p-4">
        {renderContent()}
      </div>
    </div>
  );
}

export { ContractorsLayout };
